import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { KlaraLogEntry } from '../../../model/klara';
import { useAppTranslation } from '../../../services/i18n';
import { MUTED_TEXT, CARD_BORDER, LOG_RULE, SECTION_BACK } from '../colors';

type Props = {
  entries: KlaraLogEntry[];
  active: boolean;
  stalled: boolean;
  /**
   * Sits inside the card, under the rows. The design nests the excluded-sources
   * warning there rather than beside the feed.
   */
  footer?: React.ReactNode;
};

/**
 * The "Aktuální proces" feed.
 *
 * One line per partner callback — which is what we get. The evaluation phase
 * emits two events and generation about eight, arriving in bursts with
 * multi-minute silences, so this is a burst log rather than the per-minute
 * ticker the mockup suggests. That is why the last-update line matters: it is
 * what tells the user a quiet panel is not a hung one.
 *
 * Text is generated from the event type and its structured payload, never from
 * the partner's own `detail` string — that is Czech free text we do not
 * control, and it can carry an internal document title or a stack trace.
 */
export function ProcessLog({ entries, active, stalled, footer }: Props) {
  const t = useAppTranslation();
  const newestFirst = [...entries].reverse();
  const latest = newestFirst[0];

  return (
    <Box
      sx={{
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: '8px',
        padding: '16px',
        // The rows are white, so the card cannot be — on the white tab panel
        // they would have no edge at all.
        backgroundColor: SECTION_BACK,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
          {t('klara.log.title')}
        </Typography>
        <StatusDot stalled={stalled} active={active} />
      </Box>

      {newestFirst.length === 0 && (
        <Typography sx={{ color: MUTED_TEXT }}>
          {t('klara.log.empty')}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {newestFirst.map((entry) => (
          <Box
            key={entry.id}
            sx={{
              display: 'flex',
              gap: '12px',
              padding: '11px 12px',
              backgroundColor: 'var(--color-white)',
              borderRadius: '8px',
              borderLeft: `3px solid ${LOG_RULE}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 500,
                color: MUTED_TEXT,
                minWidth: '50px',
                // The times are one line, the message may wrap; without this
                // the clock drifts down the row with it.
                flexShrink: 0,
                paddingTop: '2px',
              }}
            >
              {new Date(entry.created_at).toLocaleTimeString()}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'var(--color-black-text)' }}>
              {describe(entry, t)}
            </Typography>
          </Box>
        ))}
      </Box>

      {footer && <Box sx={{ marginTop: '12px' }}>{footer}</Box>}

      {latest && (
        <Typography
          variant="caption"
          sx={{ color: MUTED_TEXT, display: 'block', marginTop: '12px' }}
        >
          {t('klara.log.lastUpdate', {
            time: new Date(latest.created_at).toLocaleTimeString(),
          })}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Whether Klara is still working, as the dot the design draws — a filled chip
 * shouted where a dot suffices, and the three states differ only in colour.
 */
function StatusDot({ stalled, active }: { stalled: boolean; active: boolean }) {
  const t = useAppTranslation();

  const color = stalled
    ? 'var(--color-warning)'
    : active
    ? 'var(--color-success)'
    : MUTED_TEXT;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <Box
        sx={{
          width: '8px',
          height: '8px',
          borderRadius: '4px',
          backgroundColor: color,
        }}
      />
      <Typography sx={{ fontSize: '12px', color: MUTED_TEXT }}>
        {stalled
          ? t('klara.log.stalled')
          : active
          ? t('klara.log.active')
          : t('klara.log.done')}
      </Typography>
    </Box>
  );
}

/**
 * `event_type` is namespaced `generated_content.<event>`; the tail is the
 * partner's event name.
 *
 * The values inside an event — phase, verdict, artifact type — arrive raw from
 * the partner, in English, and are translated on their own before they reach a
 * sentence. What we cannot name is left out rather than printed: a line that
 * says less is better than a line with an English word in it.
 */
function describe(entry: KlaraLogEntry, t: (key: string, options?: any) => string) {
  const event = entry.event_type.replace(/^generated_content\./, '');
  const data: Record<string, unknown> = entry.metadata ?? {};
  const line = (key: string, options?: any) =>
    t(`klara.log.events.${key}`, { defaultValue: '', ...options });
  const word = (group: string, value: unknown) =>
    typeof value === 'string' ? t(`klara.log.${group}.${value}`, { defaultValue: '' }) : '';
  const number = (value: unknown) => (typeof value === 'number' ? value : undefined);

  switch (event) {
    case 'phase_started': {
      const phase = word('phases', data.phase);
      return (
        line(`phase_started_${data.phase}`) ||
        (phase ? line('phase_started', { phase }) : '') ||
        line('unknown')
      );
    }
    case 'phase_failed': {
      const phase = word('phases', data.phase);
      return phase ? line('phase_failed', { phase }) : line('failed');
    }
    // The partner's verdict list is incomplete, so an unknown one is dropped
    // rather than guessed at — the score alone still says something true.
    case 'evaluation_completed': {
      const verdict = word('verdicts', data.verdict);
      const score = number(data.theme_score);
      if (verdict && score !== undefined) return line('evaluation_completed', { verdict, score });
      if (verdict) return line('evaluation_completed_verdict', { verdict });
      if (score !== undefined) return line('evaluation_completed_score', { score });
      return line('evaluation_completed_plain');
    }
    case 'research_completed':
      return line('research_completed', { count: number(data.documents) ?? 0 });
    case 'knowledge_pack_written':
      return line('knowledge_pack_written', { count: number(data.word_count) ?? 0 });
    case 'review_iteration_completed': {
      const iteration = number(data.iteration) ?? 1;
      const score = number(data.overall_score);
      return score === undefined
        ? line('review_iteration_completed_plain', { iteration })
        : line('review_iteration_completed', { iteration, score });
    }
    case 'artifact_completed': {
      const name =
        typeof data.artifact_type === 'string'
          ? t(`klara.documentTypes.${data.artifact_type}`, { defaultValue: '' })
          : '';
      return name ? line('artifact_completed', { name }) : line('artifact_completed_other');
    }
    default:
      return line(event, data) || line('unknown');
  }
}
